import { InnerhivePage } from '../InnerhivePage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { Redirect, Route } from 'react-router';
import { AppointmentsList } from './AppointmentsList';
import { AppointmentEditorDrawer } from './AppointmentEditorDrawer';
import { IonButtons, IonToolbar } from '@ionic/react';
import { AddAppointmentButton } from './AddAppointmentButton';
import { AppointmentsState, NEW_APPOINTMENT_ID } from './AppointmentsState';
import { useEffect, useRef, useState } from 'react';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { CalendarTabsProvider } from './CalendarTabsProvider';
import { FullWidthHeader } from '../components/FullWidthHeader';
import { AppointmentsConfigPopover } from './AppointmentsConfigPopover';
import { useSubscription } from '@meraki-internal/state';
import { Text } from '../components/Text';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { Href } from '../components/Href';
import { ConnectCalendarViewModel } from './ConnectCalendarViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { ViewNoteWithQueryParamDrawer } from '../notes/view-note/ViewNoteWithQueryParamDrawer';
import { AppointmentsFilterControl, AppointmentsFilterState } from './AppointmentsFilterControl';
import { useBreakpoints } from '../theme/useBreakpoints';
import { HistoryViewModel } from '../support/HistoryViewModel';

export const AppointmentsPageRouteHandler: React.FC = () => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);
    const history = useInstance(HistoryViewModel);

    const recentCareMap = careMapState.getMostRecentCaremap();

    return (
        <>
            <Route exact path="/appointments/:caremapId" render={routeParams => (
                <Redirect to={`/calendar/appointments/${routeParams.match.params.caremapId}`} />
            )} />

            <Route exact path="/calendar/appointments" render={() => {
                // Preserve query params when redirecting to caremap-specific route
                const currentSearch = history.getCurrentLocation().search || '';
                if (recentCareMap) {
                    return <Redirect to={`/calendar/appointments/${recentCareMap.careMapId}${currentSearch}`} />;
                } else {
                    return <AppointmentsNoCareMapPage />;
                }
            }} />

            <Route exact path="/calendar/appointments/:caremapId" render={routeParams => (
                <ActiveCareMapProviderV2 careMapId={routeParams.match.params.caremapId}>
                    <ActiveCaremapAppointmentsPage />
                </ActiveCareMapProviderV2>
            )} />
        </>

    );

};

const emptyImage = <img src="/assets/images/calendar/empty-appointments.svg" alt="no appointments" style={{width: '100%'}} />;

export const AppointmentsNoCareMapPage: React.FC = () => {
    const tabsProvider = useInstance(CalendarTabsProvider);
    return (
        <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs(undefined)}>
            <CareRecipientSwitcher
                trackingFrom="Appointments"
                createAccountMsg="Create an account to organize your care schedule and even sync to your calendar app of choice!"
                addRecipientMsg="Add a care recipient to organize your care schedule and even sync to your calendar app of choice!"
            />
            {emptyImage}
        </InnerhivePage>
    );
};

export const ActiveCaremapAppointmentsPage: React.FC = () => {
    const drawer = useInstance(AppointmentEditorDrawer);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const tabsProvider = useInstance(CalendarTabsProvider);
    const connectCalDialogVM = useInstance(ConnectCalendarViewModel);
    const careMapState = useInstance(CareMapState);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);

    const appointmentsState = useInstance(AppointmentsState);
    useSubscription(() => appointmentsState);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const contentRef = useRef<HTMLIonContentElement>(null);

    const [filters, setFilters] = useState<AppointmentsFilterState>({
        tags: []
    });

    const { isBelowBreakpoint } = useBreakpoints();
    const isMobile = isBelowBreakpoint('md');

    // NOTE: heightOffset accounts for the fixed header containing switcher,
    // which is taller for anonymous due to expiration message
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const heightOffset = 80 + (isAnonymous ? 28 : 0);

    const hasInnerhivePlus = revenueCat.hasInnerhivePlus();
    const emptyText = hasInnerhivePlus ? (
        <>
            Track appointments to organize your care schedule and even <Href onClick={() => connectCalDialogVM.open('empty appointments')}>sync to your calendar app</Href> of choice!
        </>
    ) : (
        <>
            Track appointments to organize your care schedule and even sync to your calendar app of choice!
        </>
    );

    useEffect(() => {
        mixpanel.track('Appointments Tab Opened');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const editAppointment = (appointmentId: string) => {
        if (appointmentId === NEW_APPOINTMENT_ID) {
            appointmentsState.trackNewAppointmentOpened({from: 'Appointments Page'});
        }

        drawer.open({ appointmentId });
    };

    const activeCaremap = careMapState.getActiveCaremap();
    const isIncomplete = !activeCaremap || careMapState.isIncomplete(activeCaremap);

    // Get appointments for filtering
    const allAppointments = appointmentsState.state.appointments || [];


    // Apply tag filters
    const filteredAppointments = allAppointments.filter(appointment => {
        if (filters.tags.length === 0) {
            return true;
        }

        if (!appointment.tags || appointment.tags.length === 0) {
            return false;
        }

        return filters.tags.some(filterTag =>
            appointment.tags!.some(appointmentTag =>
                appointmentTag.toLowerCase().includes(filterTag.toLowerCase())
            )
        );
    });

    return (
        <>
            <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs(activeCaremap)} contentRef={contentRef}>
                <FullWidthHeader fixed>
                    <IonToolbar>
                        <CareRecipientSwitcher
                            trackingFrom="Appointments"
                            createAccountMsg="Create an account to track appointments!"
                            addRecipientMsg="Add a care recipient to track appointments!"
                        />

                        {!isIncomplete &&
                            <IonButtons slot="end">
                                {allAppointments.length > 0 && (
                                    <AppointmentsFilterControl
                                        filters={filters}
                                        onFiltersChange={setFilters}
                                        appointments={allAppointments}
                                        triggerId="appointments-filter-trigger"
                                        iconOnly={isMobile}
                                    />
                                )}
                                <AddAppointmentButton
                                    useIcon
                                    onClick={editAppointment}
                                    canAdd={appointmentsState.canAdd()}
                                    iconOnly={isMobile}
                                />
                            </IonButtons>
                        }
                    </IonToolbar>
                </FullWidthHeader>

                <AppointmentsList
                    hideHeading
                    showEmptyToday
                    heightOffset={heightOffset}
                    contentRef={contentRef}
                    data-type="more-appointments-list"
                    editAppointment={editAppointment}
                    onViewRecording={(noteId) => {
                        viewNoteDrawer.open({ noteId, trackingFrom: 'Appointments Page' });
                    }}
                    customEmptyMsg={(
                        <div style={{paddingTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <div><Text>{emptyText}</Text></div>
                            {emptyImage}
                        </div>
                    )}
                    filteredAppointments={filteredAppointments}
                />
                <AppointmentsConfigPopover />

            </InnerhivePage>
        </>
    );
};
